import React, { useMemo } from "react";
import { ShieldCheck } from "lucide-react";
import { useRoles } from "@/hooks/useRoles";
import { Switch } from "@/components/ui/switch";

// Capability-based, role-aware permission editor. The list of capabilities and their
// expanded raw-permission bundles comes from the backend (`capabilities_by_role`), so a
// single toggle like "View own statistics" grants the right scope for the target role.
// A capability is ON when ALL its raw perms are effectively present.
export function BusinessPermissions({ role, granted = [], revoked = [], onChange }) {
  const { data } = useRoles();
  const roles = data?.roles || [];
  const roleDoc = roles.find((r) => r.id === role);
  const caps = (data?.capabilities_by_role || {})[role] || [];
  const inherited = useMemo(() => new Set(roleDoc?.permissions || []), [roleDoc]);
  const isSuper = (roleDoc?.permissions || []).includes("*");

  const gSet = new Set(granted);
  const rSet = new Set(revoked);
  const effective = (p) => (inherited.has(p) && !rSet.has(p)) || gSet.has(p);
  const capOn = (cap) => cap.perms.length > 0 && cap.perms.every(effective);

  const toggleCap = (cap) => {
    const g = new Set(granted);
    const r = new Set(revoked);
    const turnOn = !capOn(cap);
    cap.perms.forEach((p) => {
      if (turnOn) { if (inherited.has(p)) r.delete(p); else g.add(p); }
      else { if (inherited.has(p)) r.add(p); else g.delete(p); }
    });
    onChange([...g], [...r]);
  };

  if (isSuper) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-[#22C55E]/30 bg-[#22C55E]/10 px-3 py-3 text-sm text-[#22C55E]">
        <ShieldCheck className="h-4 w-4" /> This role has full access and cannot be restricted.
      </div>
    );
  }

  const groups = [];
  caps.forEach((c) => {
    let grp = groups.find((g) => g.name === c.group);
    if (!grp) { grp = { name: c.group, items: [] }; groups.push(grp); }
    grp.items.push(c);
  });

  return (
    <div className="space-y-4" data-testid="business-permissions">
      {groups.map((grp) => (
        <div key={grp.name} className="rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">{grp.name}</p>
          <div className="space-y-2.5">
            {grp.items.map((cap) => {
              const checked = capOn(cap);
              const custom = checked && !cap.perms.every((p) => inherited.has(p));
              return (
                <div key={cap.key} className="flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-foreground/90">
                      {cap.label}
                      {custom && <span className="rounded bg-[#3B82F6]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#3B82F6]">Custom</span>}
                    </div>
                    {cap.hint && <div className="text-xs text-muted-foreground">{cap.hint}</div>}
                  </div>
                  <Switch data-testid={`perm-${cap.key}`} checked={checked} onCheckedChange={() => toggleCap(cap)} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
